// Hero2.js - Contains functions for creating a modern Hero section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHeroElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'hero2-section';
  const heroContainerClass = 'hero2-container';
  const heroContentClass = 'hero2-content';
  const heroTitleClass = 'hero2-title';
  const heroHighlightClass = 'hero2-highlight';
  const heroSubtitleClass = 'hero2-subtitle';
  const heroButtonsClass = 'hero2-buttons';
  const heroPrimaryBtnClass = 'hero2-primary-btn';
  const heroSecondaryBtnClass = 'hero2-secondary-btn';
  const heroImageWrapperClass = 'hero2-image-wrapper';
  const heroImageClass = 'hero2-image';
  const heroOverlayClass = 'hero2-overlay';
  const heroStatsCtnClass = 'hero2-stats-container';
  const heroStatClass = 'hero2-stat';
  const heroStatNumberClass = 'hero2-stat-number';
  const heroStatLabelClass = 'hero2-stat-label';

  // Desktop Styles (default)
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#111827',
    background: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden'
  };

  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '1280px',
    width: '100%',
    margin: '0 auto',
    gap: '60px',
    position: 'relative',
    zIndex: '1'
  };

  const heroContentStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '600px',
    position: 'relative',
    zIndex: '2'
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '56px',
    fontWeight: '800',
    color: '#ffffff',
    lineHeight: '1.1',
    letterSpacing: '-0.02em'
  };

  const heroHighlightStyles = {
    color: '#10b981',
    position: 'relative',
    display: 'inline-block'
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '20px',
    fontWeight: '400',
    color: '#d1d5db',
    lineHeight: '1.6',
    maxWidth: '540px'
  };

  const heroButtonsStyles = {
    display: 'flex',
    gap: '16px',
    marginTop: '12px'
  };

  const heroPrimaryBtnStyles = {
    padding: '14px 32px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.2)'
  };

  const heroSecondaryBtnStyles = {
    padding: '14px 32px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(5px)'
  };

  const heroImageWrapperStyles = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '500px',
    position: 'relative',
    zIndex: '2'
  };

  const heroImageStyles = {
    width: '100%',
    maxWidth: '550px',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    transform: 'perspective(1000px) rotateY(-10deg) rotateX(5deg)',
    transition: 'transform 0.5s ease',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const heroOverlayStyles = {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '60%',
    height: '100%',
    background: 'radial-gradient(circle at 70% 50%, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0) 70%)',
    zIndex: '0'
  };

  const heroStatsCtnStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '48px',
    padding: '24px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const heroStatStyles = {
    textAlign: 'center',
    padding: '0 12px'
  };

  const heroStatNumberStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#10b981',
    marginBottom: '4px'
  };

  const heroStatLabelStyles = {
    fontSize: '14px',
    color: '#d1d5db',
    fontWeight: '500'
  };

  // Tablet Styles
  const tabletHeroContainerStyles = {
    ...heroContainerStyles,
    gap: '40px',
    padding: '0 20px'
  };

  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '44px'
  };

  const tabletHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '420px'
  };

  const tabletHeroStatsCtnStyles = {
    ...heroStatsCtnStyles,
    padding: '20px'
  };

  const tabletHeroStatNumberStyles = {
    ...heroStatNumberStyles,
    fontSize: '30px'
  };

  // Mobile Styles
  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column',
    padding: '0 15px',
    gap: '40px'
  };

  const mobileHeroContentStyles = {
    ...heroContentStyles,
    alignItems: 'center',
    textAlign: 'center'
  };

  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '36px'
  };

  const mobileHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '18px'
  };

  const mobileHeroButtonsStyles = {
    ...heroButtonsStyles,
    justifyContent: 'center'
  };

  const mobileHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '100%'
  };

  const mobileHeroImageStyles = {
    ...heroImageStyles,
    transform: 'none'
  };

  const mobileHeroStatsCtnStyles = {
    ...heroStatsCtnStyles,
    flexDirection: 'column',
    gap: '16px'
  };

  const mobileHeroStatStyles = {
    ...heroStatStyles,
    padding: '10px 0'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContentClass, heroContentStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroHighlightClass, heroHighlightStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonsClass, heroButtonsStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPrimaryBtnClass, heroPrimaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSecondaryBtnClass, heroSecondaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageWrapperClass, heroImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageClass, heroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(heroOverlayClass, heroOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatsCtnClass, heroStatsCtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatClass, heroStatStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatNumberClass, heroStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatLabelClass, heroStatLabelStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroImageWrapperClass, tabletHeroImageWrapperStyles);
    newRegistry[DEVICES.TABLET].set(heroStatsCtnClass, tabletHeroStatsCtnStyles);
    newRegistry[DEVICES.TABLET].set(heroStatNumberClass, tabletHeroStatNumberStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroContentClass, mobileHeroContentStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroButtonsClass, mobileHeroButtonsStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageWrapperClass, mobileHeroImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageClass, mobileHeroImageStyles);
    newRegistry[DEVICES.MOBILE].set(heroStatsCtnClass, mobileHeroStatsCtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroStatClass, mobileHeroStatStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroContainerClass,
    heroContentClass,
    heroTitleClass,
    heroHighlightClass,
    heroSubtitleClass,
    heroButtonsClass,
    heroPrimaryBtnClass,
    heroSecondaryBtnClass,
    heroImageWrapperClass,
    heroImageClass,
    heroOverlayClass,
    heroStatsCtnClass,
    heroStatClass,
    heroStatNumberClass,
    heroStatLabelClass
  ]));

  // Generate IDs for all elements
  const heroSectionId = Date.now();
  const containerID = Date.now() + 1;
  const contentID = Date.now() + 2;
  const titleID = Date.now() + 3;
  const highlightTextID = Date.now() + 4;
  const subtitleID = Date.now() + 5;
  const buttonsWrapperID = Date.now() + 6;
  const primaryButtonID = Date.now() + 7;
  const secondaryButtonID = Date.now() + 8;
  const imageWrapperID = Date.now() + 9;
  const heroImageID = Date.now() + 10;
  const overlayID = Date.now() + 11;
  const statsContainerID = Date.now() + 12;
  const stat1ID = Date.now() + 13;
  const stat1NumberID = Date.now() + 14;
  const stat1LabelID = Date.now() + 15;
  const stat2ID = Date.now() + 16;
  const stat2NumberID = Date.now() + 17;
  const stat2LabelID = Date.now() + 18;
  const stat3ID = Date.now() + 19;
  const stat3NumberID = Date.now() + 20;
  const stat3LabelID = Date.now() + 21;

  const newElements = [
    // Main Hero Section Container
    {
      id: heroSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Hero Section 2',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [containerID, overlayID],
      text: '',
      attributes: {
        'data-element': 'hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    
    // Background Overlay
    {
      id: overlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Background Overlay',
      parentId: heroSectionId,
      hierarchyName: "Background Overlay",
      className: heroOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-overlay'
      }
    },
    
    // Container for content and image
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      hierarchyName: "Hero Container",
      className: heroContainerClass,
      styles: {},
      children: [contentID, imageWrapperID],
      text: '',
      attributes: {
        'data-element': 'hero-container'
      }
    },
    
    // Content area (left side)
    {
      id: contentID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Content',
      parentId: containerID,
      hierarchyName: "Hero Content",
      className: heroContentClass,
      styles: {},
      children: [titleID, subtitleID, buttonsWrapperID, statsContainerID],
      text: '',
      attributes: {
        'data-element': 'hero-content'
      }
    },
    
    // Title
    {
      id: titleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: contentID,
      hierarchyName: "Hero Title",
      className: heroTitleClass,
      styles: {},
      children: [highlightTextID],
      text: 'Create stunning websites ',
      attributes: {
        'data-element': 'hero-title'
      },
      interactions: {
        events: [{
          id: Date.now(),
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '0.8s',
            delay: '0.1s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Highlight Text Span
    {
      id: highlightTextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Highlight Text',
      parentId: titleID,
      hierarchyName: "Highlight Text",
      className: heroHighlightClass,
      styles: {},
      children: [],
      text: 'without code',
      attributes: {
        'data-element': 'hero-highlight-text'
      }
    },
    
    // Subtitle
    {
      id: subtitleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Hero Subtitle',
      parentId: contentID,
      hierarchyName: "Hero Subtitle",
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'Our intuitive drag-and-drop builder empowers you to design professional websites in minutes. No coding skills required — just your creativity.',
      attributes: {
        'data-element': 'hero-subtitle'
      },
      interactions: {
        events: [{
          id: Date.now() + 1,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '0.8s',
            delay: '0.3s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Buttons Wrapper
    {
      id: buttonsWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Buttons',
      parentId: contentID,
      hierarchyName: "Hero Buttons",
      className: heroButtonsClass,
      styles: {},
      children: [primaryButtonID, secondaryButtonID],
      text: '',
      attributes: {
        'data-element': 'hero-buttons'
      }
    },
    
    // Primary Button
    {
      id: primaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Primary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Primary Button",
      className: heroPrimaryBtnClass,
      styles: {},
      children: [],
      text: 'Start Building Free',
      attributes: {
        'data-element': 'primary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: Date.now() + 2,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '0.8s',
            delay: '0.5s',
            iterations: '1'
          },
        },
        {
          id: Date.now() + 3,
          type: 'animation',
          action: 'animate',
          trigger: 'onHover',
          value: {
            animation: 'pulse',
            duration: '0.6s',
            delay: '0s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Secondary Button
    {
      id: secondaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Secondary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Secondary Button",
      className: heroSecondaryBtnClass,
      styles: {},
      children: [],
      text: 'Watch Demo',
      attributes: {
        'data-element': 'secondary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: Date.now() + 4,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '0.8s',
            delay: '0.6s',
            iterations: '1'
          }
        },
        {
          id: Date.now() + 5,
          type: 'animation',
          action: 'animate',
          trigger: 'onHover',
          value: {
            animation: 'pulse',
            duration: '0.6s',
            delay: '0s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Stats Container
    {
      id: statsContainerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stats Container',
      parentId: contentID,
      hierarchyName: "Stats Container",
      className: heroStatsCtnClass,
      styles: {},
      children: [stat1ID, stat2ID, stat3ID],
      text: '',
      attributes: {
        'data-element': 'hero-stats-container'
      },
      interactions: {
        events: [{
          id: Date.now() + 6,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '0.8s',
            delay: '0.8s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Stat 1
    {
      id: stat1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1',
      parentId: statsContainerID,
      hierarchyName: "Stat 1",
      className: heroStatClass,
      styles: {},
      children: [stat1NumberID, stat1LabelID],
      text: '',
      attributes: {
        'data-element': 'hero-stat'
      }
    },
    
    // Stat 1 Number
    {
      id: stat1NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1 Number',
      parentId: stat1ID,
      hierarchyName: "Stat 1 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '10k+',
      attributes: {
        'data-element': 'hero-stat-number'
      }
    },
    
    // Stat 1 Label
    {
      id: stat1LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1 Label',
      parentId: stat1ID,
      hierarchyName: "Stat 1 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Active Users',
      attributes: {
        'data-element': 'hero-stat-label'
      }
    },
    
    // Stat 2
    {
      id: stat2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2',
      parentId: statsContainerID,
      hierarchyName: "Stat 2",
      className: heroStatClass,
      styles: {},
      children: [stat2NumberID, stat2LabelID],
      text: '',
      attributes: {
        'data-element': 'hero-stat'
      }
    },
    
    // Stat 2 Number
    {
      id: stat2NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2 Number',
      parentId: stat2ID,
      hierarchyName: "Stat 2 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '250+',
      attributes: {
        'data-element': 'hero-stat-number'
      }
    },
    
    // Stat 2 Label
    {
      id: stat2LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2 Label',
      parentId: stat2ID,
      hierarchyName: "Stat 2 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Templates',
      attributes: {
        'data-element': 'hero-stat-label'
      }
    },
    
    // Stat 3
    {
      id: stat3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3',
      parentId: statsContainerID,
      hierarchyName: "Stat 3",
      className: heroStatClass,
      styles: {},
      children: [stat3NumberID, stat3LabelID],
      text: '',
      attributes: {
        'data-element': 'hero-stat'
      }
    },
    
    // Stat 3 Number
    {
      id: stat3NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3 Number',
      parentId: stat3ID,
      hierarchyName: "Stat 3 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '99%',
      attributes: {
        'data-element': 'hero-stat-number'
      }
    },
    
    // Stat 3 Label
    {
      id: stat3LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3 Label',
      parentId: stat3ID,
      hierarchyName: "Stat 3 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Satisfaction',
      attributes: {
        'data-element': 'hero-stat-label'
      }
    },
    
    // Image Wrapper (right side)
    {
      id: imageWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Image Wrapper',
      parentId: containerID,
      hierarchyName: "Hero Image Wrapper",
      className: heroImageWrapperClass,
      styles: {},
      children: [heroImageID],
      text: '',
      attributes: {
        'data-element': 'hero-image-wrapper'
      }
    },
    
    // Hero Image
    {
      id: heroImageID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Hero Image',
      parentId: imageWrapperID,
      hierarchyName: "Hero Image",
      className: heroImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1581276879432-15e50529f34b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80',
      attributes: {
        'data-element': 'hero-image',
        'alt': 'Website builder in action'
      },
      interactions: {
        events: [{
          id: Date.now() + 7,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-left',
            duration: '1s',
            delay: '0.3s',
            iterations: '1'
          }
        }]
      }
    }
  ];
  
  // Return all elements
  return {
    heroSection: newElements[0],
    elements: newElements
  };
};

export default createHeroElements; 